<template>
  <!--
    SingleWindow: layout de la Ventanilla Única. Reutiliza la base de
    FullWidthLayout (header fijo con zindex, Navbar, Breadcrumb y Footer con
    sticky structure).
      Cabecera blanca: título + subtítulo de la pantalla.
      Banda gris:      router-view; cada vista hija define su propio contenido.
    El layout solo aporta la estructura y el color de fondo de cada banda.
  -->
  <div class="min-h-screen flex flex-col bg-white">
    <div class="fixed w-full zindex">
      <Header class="zindex" />
      <div class="w-full bg-white">
        <div class="border-bottom">
          <Navbar class="py-3" typeContainer="container-workspace" />
        </div>
        <Breadcrumb class="pb-0 container-workspace" />
      </div>
    </div>

    <div class="flex-1 w-full max-w-full pt-2 bg-white relative overflow-x-hidden">
      <!--
        Aviso informativo dismissible. Solo se muestra en la raíz de la Ventanilla
        (/single-window) y una única vez por sesión del navegador: al cerrarlo se
        persiste la marca en sessionStorage, de modo que no reaparece mientras dure
        la sesión (sí vuelve a mostrarse en una sesión nueva del navegador).
      -->
      <section v-if="showNotice" class="w-full bg-white pt-6 md:pt-8">
        <!-- Mismo ancho máximo que las tarjetas del home (single-window-container: 960px) -->
        <div class="sw-notice-container mx-auto w-full px-5 sm:px-6 lg:px-8">
          <div
            role="status"
            class="flex items-center gap-3 rounded-xl border border-blue bg-blue-light px-5 py-4"
          >
            <svg
              class="flex-shrink-0 text-blue mt-0.5"
              :style="{ width: '36px', height: '36px' }"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
              <path d="M12 11v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              <circle cx="12" cy="7.75" r="1.1" fill="currentColor" />
            </svg>

            <p class="flex-1 text-left text-sm text-blue leading-relaxed m-0">
              Nos modernizamos para que puedas enviar tus solicitudes y conocer, paso a paso, cómo
              realizar cada trámite de manera fácil y segura.
              <strong>
                Recuerda que el correo super@superfinanciera.gov.co,
                jurisdiccionales@superfinanciera.gov.co ya no está disponible.
              </strong>
            </p>

            <button
              type="button"
              class="flex-shrink-0 self-center text-blue hover:text-blue-dark focus:outline-none focus:ring-2 focus:ring-blue rounded"
              aria-label="Cerrar aviso"
              @click="dismissNotice"
            >
              <svg
                :style="{ width: '36px', height: '36px' }"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <!-- Cabecera blanca: título y subtítulo de la pantalla -->
      <section class="w-full bg-white pt-6 pb-8 md:pt-8 md:pb-10">
        <div class="container-workspace w-full flex-col px-4 md:px-6">
          <h1
            class="w-full text-center text-3xl md:text-4xl font-bold text-blue-dark m-0"
          >
            {{ title }}
          </h1>
          <p
            v-if="subtitle"
            class="w-full text-center text-base md:text-lg font-semibold text-gray-700 mt-2 mb-0"
          >
            {{ subtitle }}
          </p>
        </div>
      </section>

      <!-- Banda de contenido: color de superficie #f6f8f9. Cada vista define su propio componente. -->
      <section class="w-full bg-sw py-12 md:py-16">
        <div class="container-workspace w-full flex-col px-4 md:px-6">
          <transition name="slide-fade" mode="out-in" appear>
            <router-view class="relative" />
          </transition>
        </div>
      </section>

      <!-- Banda de accesos rápidos: barra gris a lo ancho, 3 enlaces distribuidos -->
      <section class="w-full bg-white my-12">
        <div class="container-workspace w-full flex-col px-0">
          <nav
            class="w-full bg-sw rounded-xl px-6 py-6 md:px-6 md:py-8"
            aria-label="Accesos rápidos"
          >
            <ul class="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 m-0 p-0 list-none">
              <li v-for="link in quickLinks" :key="link.title">
                <a
                  :href="link.href"
                  :target="link.external ? '_blank' : undefined"
                  :rel="link.external ? 'noopener noreferrer' : undefined"
                  class="group flex items-start gap-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue"
                >
                  <span
                    class="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-blue-light"
                    aria-hidden="true"
                  >
                    <img :src="link.icon" alt="" class="w-7 h-7" />
                  </span>
                  <span class="flex flex-col text-left">
                    <span class="text-base font-bold text-blue-dark no-underline">
                      {{ link.title }}
                    </span>
                    <span class="text-sm text-gray-800 no-underline mt-1">
                      {{ link.description }}
                    </span>
                  </span>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </section>

      <!-- Aviso de privacidad: fuera del bg-sw, sobre fondo blanco -->
      <section class="w-full bg-white mb-12">
        <div class="container-workspace w-full flex-col px-4 md:px-0">
          <p class="w-full text-left text-sm text-gray-500 leading-relaxed m-0">
            Protegemos sus datos personales de forma segura y transparente, conforme a la Ley 1581
            de 2012. La información que suministra es utilizada únicamente para gestionar sus
            solicitudes, brindar respuesta a sus trámites, cumplir obligaciones legales y mejorar la
            atención ofrecida por la entidad. Usted puede consultar, actualizar, corregir o solicitar
            la eliminación de su información personal a través de nuestros canales de atención
            presenciales, telefónicos o al correo electrónico
            <a
              :href="`mailto:${privacyEmail}`"
              class="text-blue hover:underline focus:underline focus:outline-none focus:ring-2 focus:ring-blue"
            >{{ privacyEmail }}</a>
            Consulte nuestra
            <a
              :href="privacyPolicyHref"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue hover:underline focus:underline focus:outline-none focus:ring-2 focus:ring-blue"
            >política de tratamiento de datos personales</a>
            para conocer sus derechos, las finalidades del tratamiento y el uso que damos a su
            información.
          </p>
        </div>
      </section>
    </div>

    <Footer />
  </div>
</template>

<script>
import Navbar from "@/components/UI/Navbar/Navbar";
import Header from "@/components/UI/Header";
import Footer from "@/components/UI/Footer";
import Breadcrumb from "@/components/UI/Breadcrumb";
import opinionsIcon from "@/assets/icons/opiniones.svg";
import channelsIcon from "@/assets/icons/canales-atencion.svg";
import guidesIcon from "@/assets/icons/guias-consumidor.svg";

// Clave de sessionStorage para recordar que el aviso fue cerrado en esta sesión.
const NOTICE_KEY = "sw_home_notice_dismissed";

export default {
  name: "SingleWindow",
  components: {
    Navbar,
    Header,
    Footer,
    Breadcrumb,
  },
  data() {
    return {
      // Accesos rápidos: cada bloque tiene su enlace asociado.
      quickLinks: [
        {
          title: "Ver opiniones",
          description: "Consulte los resultados de satisfacción",
          icon: opinionsIcon,
          href: "https://www.superfinanciera.gov.co/publicaciones/60866/atencion-y-servicios-a-la-ciudadaniaatencion-al-ciudadanoencuesta-de-satisfaccion-retroalimentacion-de-partes-interesadas-60866/",
          external: true,
        },
        {
          title: "Ver canales de atención",
          description: "Consulte nuestros canales de atención",
          icon: channelsIcon,
          href: "https://www.superfinanciera.gov.co/publicaciones/10097788/atencion-y-servicios-a-la-ciudadaniacanales-de-atencion-10097788/",
          external: true,
        },
        {
          title: "Revisar guías",
          description: "Diseñadas para el consumidor financiero",
          icon: guidesIcon,
          href: "https://www.superfinanciera.gov.co/publicaciones/10114649/personasguias-para-el-consumidor-financiero-10114649/",
          external: true,
        },
      ],
      privacyEmail: "proteccióndatos@superfinanciera.gov.co",
      privacyPolicyHref: "https://www.superfinanciera.gov.co/publicaciones/10106783/nuestra-entidadacerca-de-la-sfcpoliticas-de-la-entidadpoliticas-web-10106783/",
      // Marca de que el usuario cerró el aviso en esta sesión del navegador.
      noticeDismissed: false,
    };
  },
  created() {
    try {
      this.noticeDismissed = sessionStorage.getItem(NOTICE_KEY) === "1";
    } catch (e) {
      // Sin sessionStorage disponible: tratamos el aviso como no cerrado.
      this.noticeDismissed = false;
    }
  },
  computed: {
    title() {
      return this.$route.meta?.title || "Ventanilla Superfinanciera";
    },
    subtitle() {
      return this.$route.meta?.subtitle || "Canal de recepción digital para sus trámites";
    },
    // Solo la raíz de la Ventanilla (/single-window) muestra el aviso.
    isHome() {
      const path = (this.$route.path || "").replace(/\/+$/, "");
      return path === "/single-window";
    },
    // El aviso se muestra solo en el home y si no fue cerrado en esta sesión.
    showNotice() {
      return this.isHome && !this.noticeDismissed;
    },
  },
  methods: {
    dismissNotice() {
      this.noticeDismissed = true;
      try {
        sessionStorage.setItem(NOTICE_KEY, "1");
      } catch (e) {
        // Sin sessionStorage: el aviso se oculta igual durante esta sesión en memoria.
      }
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #ddd;
}
.zindex {
  z-index: 999;
  position: relative;
}
/* Mismo ancho máximo que .single-window-container del home, para que el aviso
   quede alineado con las tarjetas de opciones. */
.sw-notice-container {
  max-width: 960px;
}
</style>
